import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Check, Package, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export const EnquiryButton = ({ business, user }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', phone: '', company: user?.organisation || '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!business?.claimed) return null;
  if (!user) {
    return <Link to={`/login?next=${encodeURIComponent(`/b/${business.id}`)}`} className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full border border-ink text-ink font-body text-sm font-semibold hover:bg-ink hover:text-white transition-colors" data-testid="profile-enquire-login"><MessageSquare className="w-3.5 h-3.5" /> Send an enquiry</Link>;
  }
  if (business.is_owner) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/enquiries', { business_id: business.id, ...form });
      setSent(true);
      toast.success('Enquiry sent to the business owner');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send enquiry'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="profile-enquire-btn" className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full border border-ink text-ink font-body text-sm font-semibold hover:bg-ink hover:text-white transition-colors"><MessageSquare className="w-3.5 h-3.5" /> Send an enquiry</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="enquiry-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg font-extrabold text-ink">Enquire with {business.company_name}</DialogTitle></DialogHeader>
          {sent ? (
            <div className="py-6 text-center" data-testid="enquiry-sent">
              <div className="w-12 h-12 rounded-full bg-forest/10 mx-auto flex items-center justify-center mb-3"><Check className="w-6 h-6 text-forest" /></div>
              <p className="font-body text-sm text-ink">Sent. The owner has been emailed and will reply to <strong>{user.email}</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Input required minLength={3} maxLength={120} placeholder="Subject (e.g. Quote for 20 units)" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="font-body text-sm" data-testid="enquiry-subject" />
              <Textarea required minLength={10} maxLength={2000} rows={5} placeholder="Tell them what you need, quantities, timelines…" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="font-body text-sm" data-testid="enquiry-message" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Your company (optional)" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="font-body text-sm" data-testid="enquiry-company" />
                <Input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="font-body text-sm" data-testid="enquiry-phone" />
              </div>
              <p className="font-body text-[11px] text-ink/50">Sent from {user.email}. Logged in Adoptic and our CRM so the owner can follow up.</p>
              <Button type="submit" disabled={busy} className="w-full bg-brand-orange hover:bg-highlight-orange text-white font-body text-sm" data-testid="enquiry-submit">{busy ? 'Sending…' : 'Send enquiry'}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const STATUS_STYLE = { new: 'bg-brand-orange/10 text-brand-orange', read: 'bg-ink/5 text-ink/60', replied: 'bg-forest/10 text-forest', closed: 'bg-ink/5 text-ink/40' };

export const OwnerEnquiries = ({ businessId }) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/enquiries/received/${businessId}`).then(r => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => { await api.put(`/enquiries/${id}/status`, null, { params: { status } }); load(); };
  if (!rows) return <div className="solid-card p-6 font-body text-sm text-mid">Loading…</div>;
  return (
    <div className="solid-card p-5" data-testid="owner-enquiries">
      <h3 className="font-display text-base font-extrabold text-ink mb-1">Buyer enquiries</h3>
      <p className="font-body text-xs text-mid mb-4">Messages from buyers and partners who found you on Adoptic. Reply directly by email.</p>
      {rows.length === 0 && <p className="font-body text-sm text-mid" data-testid="owner-enquiries-empty">No enquiries yet — verified profiles rank first, so keep your listings fresh.</p>}
      <div className="space-y-3">
        {rows.map(e => (
          <div key={e.id} className="rounded-[10px] border border-warm-border p-4" data-testid={`enquiry-${e.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-body text-sm font-semibold text-ink">{e.subject}</p>
              <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[e.status] || ''}`}>{e.status}</span>
            </div>
            <p className="font-body text-xs text-ink-2 mt-1 whitespace-pre-wrap">{e.message}</p>
            <div className="flex flex-wrap items-center gap-3 mt-3 font-body text-xs text-mid">
              <span>{e.buyer_name}{e.buyer_company ? ` · ${e.buyer_company}` : ''}</span>
              <a href={`mailto:${e.buyer_email}?subject=Re: ${encodeURIComponent(e.subject)}`} className="inline-flex items-center gap-1 text-brand-orange font-semibold" onClick={() => setStatus(e.id, 'replied')}><Mail className="w-3 h-3" /> Reply</a>
              {e.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</span>}
              <span className="ml-auto">{new Date(e.created_at).toLocaleString()}</span>
              {e.status !== 'closed' && <button onClick={() => setStatus(e.id, 'closed')} className="underline">Close</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const OwnerOrders = ({ businessId }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/billing/orders/business/${businessId}`).then(r => setData(r.data)).catch(() => setData({ orders: [] })); }, [businessId]);
  if (!data) return <div className="solid-card p-6 font-body text-sm text-mid">Loading…</div>;
  const live = data.orders.filter(o => o.stage !== 'pending_payment' && o.stage !== 'cancelled');
  return (
    <div className="solid-card p-5" data-testid="owner-orders">
      <h3 className="font-display text-base font-extrabold text-ink mb-1">Boost orders</h3>
      <p className="font-body text-xs text-mid mb-4">Track your purchased audits, listings and uplift programmes.</p>
      {live.length === 0 && <p className="font-body text-sm text-mid" data-testid="owner-orders-empty">No paid orders yet. Use <strong>Boost my business</strong> to book an audit, listing or full uplift.</p>}
      <div className="space-y-4">
        {live.map(o => (
          <div key={o.stripe_checkout_session_id || o.stripe_invoice_id || o.created_at} className="rounded-[10px] border border-warm-border p-4" data-testid={`order-${o.commission_type}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-body text-sm font-semibold text-ink flex items-center gap-1.5"><Package className="w-4 h-4 text-brand-orange" /> {{ audit: 'Digital Presence Audit', listing: 'Verification + Listing', uplift: 'Full Tier Uplift' }[o.commission_type] || o.commission_type}</p>
              <span className="font-body text-xs text-mid">£{(o.amount_paid_gbp / 100).toFixed(0)} · {new Date(o.paid_at || o.created_at).toLocaleDateString()}</span>
            </div>
            <ol className="grid grid-cols-4 gap-1 mt-4" data-testid="order-stepper">
              {o.stage_labels.map((label, i) => {
                const idx = o.stage_index - 1;
                const done = i < idx, current = i === idx;
                return (
                  <li key={label} className="text-center">
                    <div className={`h-1.5 rounded-full ${done || current ? 'bg-forest' : 'bg-ink/10'}`} />
                    <p className={`font-body text-[10px] mt-1.5 ${current ? 'text-forest font-semibold' : done ? 'text-ink/60' : 'text-ink/35'}`}>{label}</p>
                  </li>
                );
              })}
            </ol>
            {o.status_note && <p className="font-body text-xs text-ink-2 mt-3 bg-bg-warm rounded p-2">{o.status_note}</p>}
            {o.report_url && <a href={o.report_url} target="_blank" rel="noreferrer" className="inline-block mt-2 font-body text-xs font-semibold text-brand-orange underline" data-testid="order-report-link">Download report</a>}
          </div>
        ))}
      </div>
    </div>
  );
};
